import { Component } from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterModule, NavigationEnd } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Company, CompanyService } from '../services/company.service';
import { UserService } from '../services/user.service';
import { filter } from 'rxjs/operators';
import { MicrosoftSsoService } from '../services/microsoft-sso.service';

@Component({
  selector: 'app-layout',
  imports: [
    MatSidenavModule,
    MatListModule,
    MatToolbarModule,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    CommonModule
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.css'
})
export class LayoutComponent {

  company: string | null = null;
  companyId: string | null = null;
  companyLogo: string | null = null;
  user: any;
  profilePhoto: string | null = null;
  activeSection: string = 'Dashboard';

  submenu: { [key: string]: boolean } = {
    projects: false,
    invoices: false,
    ewos: false,
    rfis: false,
    timesheet: false,
    userManagement: false
  };

  constructor(
    public authService: AuthService,
    private router: Router,
    private companyService: CompanyService,
    private userService: UserService,
    private microsoftSsoService: MicrosoftSsoService
  ) {}

  ngOnInit(): void {
    this.applyCurrentSession();

    // Track current section from route
    this.updateActiveSection(this.router.url);
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e: any) => {
      this.updateActiveSection(e.urlAfterRedirects || e.url);
    });

    // Subscribe to profile photo changes
    this.authService.profilePhoto$.subscribe(url => {
      this.profilePhoto = url;
    });

    // Subscribe to company logo changes
    this.authService.companyLogo$.subscribe(url => {
      this.companyLogo = url;
    });

    if (!this.authService.isMicrosoftSession()) {
      this.loadPasswordUserContext();
    }
  }

  async logout(): Promise<void> {
    const microsoftSession = this.authService.isMicrosoftSession();
    this.authService.logout();

    if (microsoftSession) {
      try {
        await this.microsoftSsoService.logout();
        return;
      } catch {
        // A local redirect still completes logout if the Microsoft endpoint is unavailable.
      }
    }

    await this.router.navigate(['/login']);
  }

  switchCustomer(): void {
    void this.router.navigate(['/customers']);
  }

  toggleSubmenu(menu: string): void {
    this.submenu[menu] = !this.submenu[menu];
  }

  private updateActiveSection(url: string): void {
    const path = url.split('?')[0].replace(/^\//, '');
    const segment = path.split('/')[0] || '';
    const map: Record<string, string> = {
      '': 'Dashboard',
      'projects': 'Projects',
      'invoices': 'Invoices',
      'requests': 'Requests',
      'ewos': 'EWOs',
      'timesheet': 'Timesheet',
      'roles': 'User Management',
      'user': 'User Management',
      'account': 'Account',
      'user-profile': 'Profile'
    };
    this.activeSection = map[segment] || 'Dashboard';
  }

  canViewProjects(): boolean {
  return this.authService.hasAnyRole(['Admin', 'PM', 'Offshore', 'Accounting', 'User','OutSourceAdmin','OutsourcePM','OutsourceUser']);
}

canCreateProjects(): boolean {
  return !this.authService.isReadOnly() && this.authService.hasAnyRole(['Admin']);
}

canViewInvoices(): boolean {
  return this.authService.hasAnyRole(['Admin', 'PM', 'Accounting']);
}

canCreateInvoices(): boolean {
  return !this.authService.isReadOnly() && this.authService.hasAnyRole(['Admin', 'Accounting']);
}

canViewEwos(): boolean {
  return this.authService.hasAnyRole(['Admin', 'PM', 'Accounting']);
}

canCreateEwos(): boolean {
  return !this.authService.isReadOnly() && this.authService.hasAnyRole(['Admin', 'PM']);
}

canViewRfIs(): boolean {
  return this.authService.hasAnyRole(['Admin', 'PM', 'Offshore', 'Accounting', 'User','OutSourceAdmin','OutsourcePM','OutsourceUser']);
}

canCreateIQs(): boolean {
  return !this.authService.isReadOnly()
    && this.authService.hasAnyRole(['Offshore', 'User','OutSourceAdmin','OutsourcePM','OutsourceUser']);
}

canCreateRfIs(): boolean {
  return !this.authService.isReadOnly() && this.authService.hasAnyRole(['Admin']);
}

canEditRoles(): boolean {
  return !this.authService.isReadOnly() && this.authService.hasAnyRole(['Admin','OutSourceAdmin']);
}

canViewReport(): boolean {
  return this.authService.hasAnyRole(['Admin', 'PM']);
}

canViewTimesheet(): boolean {
  return this.authService.hasAnyRole(['Admin', 'PM', 'Offshore', 'Accounting', 'User','OutSourceAdmin','OutsourcePM','OutsourceUser']);
}

canEditTimeSheet(): boolean {
  return !this.authService.isReadOnly()
    && this.authService.hasAnyRole(['Admin', 'PM', 'User','OutSourceAdmin','OutsourcePM','OutsourceUser']);
}

canAddUser(): boolean {
  return !this.authService.isReadOnly() && this.authService.hasAnyRole(['Admin','OutSourceAdmin']);
}

private applyCurrentSession(): void {
  const session = this.authService.getSession();
  this.company = session?.customer?.name ?? null;
  this.companyId = session?.customer?.id ?? null;
  this.companyLogo = session?.customer?.logoUrl ?? null;

  if (session?.authSource === 'microsoft') {
    this.user = {
      id: session.user.id,
      username: session.user.userName,
      email: session.user.email,
      firstname: session.user.firstName,
      lastname: session.user.lastName,
      roles: session.user.roles
    };
    this.authService.setCompanyLogo(this.companyLogo);
  }
}

private loadPasswordUserContext(): void {
  const userId = this.authService.getUserId();
  if (userId) {
    this.userService.getUserById(userId).subscribe({
      next: res => this.user = res,
      error: err => console.error('Failed to load user profile:', err)
    });
  }

  if (this.companyId) {
    this.companyService.getCompanyById(this.companyId).subscribe({
      next: (company: Company) => {
        this.companyLogo = company.companyLogo;
        this.authService.setCompanyLogo(company.companyLogo);
      },
      error: err => console.error('Failed to load company:', err)
    });
  }
}

}
